/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/menu.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { DataGridMenuAction } from '../types'

const CLASS_NAME_MENU = 'data-grid-menu'
const CLASS_NAME_MENU_ITEM = 'data-grid-menu-item'
const CLASS_NAME_MENU_ITEM_ICON = 'data-grid-menu-item-icon'
const CLASS_NAME_MENU_DIVIDER = 'data-grid-menu-divider'

export interface GridMenuContext {
  root: HTMLElement
  getActions: (columnKey: string) => DataGridMenuAction[]
}

export interface GridMenu {
  toggle: (button: HTMLElement, columnKey: string) => void
  close: () => void
  dispose: () => void
}

let menuCount = 0

export const createMenu = (context: GridMenuContext): GridMenu => {
  const element = document.createElement('div')
  element.classList.add(CLASS_NAME_MENU)
  element.setAttribute('role', 'menu')
  element.id = `data-grid-menu-${++menuCount}`

  let trigger: HTMLElement | null = null

  const items = (): HTMLButtonElement[] =>
    [...element.querySelectorAll<HTMLButtonElement>(`.${CLASS_NAME_MENU_ITEM}`)]

  const close = (refocus = false) => {
    if (!trigger) {
      return
    }

    trigger.setAttribute('aria-expanded', 'false')
    trigger.removeAttribute('aria-controls')
    if (refocus) {
      trigger.focus()
    }

    trigger = null
    element.remove()
    document.removeEventListener('pointerdown', onOutsidePointerDown, true)
    window.removeEventListener('scroll', onWindowChange, true)
    window.removeEventListener('resize', onWindowChange)
  }

  const onOutsidePointerDown = (event: Event) => {
    const target = event.target as Node
    if (!element.contains(target) && !trigger?.contains(target)) {
      close()
    }
  }

  // Any scroll (viewport or page) or resize invalidates the anchored position;
  // closing matches the native-select/AG Grid behavior.
  const onWindowChange = (event?: Event) => {
    if (event?.target instanceof Node && element.contains(event.target)) {
      return
    }

    close()
  }

  const onMenuKeydown = (event: KeyboardEvent) => {
    const enabled = items()
    const activeIndex = enabled.indexOf(document.activeElement as HTMLButtonElement)

    switch (event.key) {
      case 'Escape': {
        event.stopPropagation()
        close(true)
        break
      }

      case 'Tab': {
        close()
        break
      }

      case 'ArrowDown': {
        event.preventDefault()
        enabled[(activeIndex + 1) % enabled.length]?.focus()
        break
      }

      case 'ArrowUp': {
        event.preventDefault()
        enabled[(activeIndex - 1 + enabled.length) % enabled.length]?.focus()
        break
      }

      case 'Home': {
        event.preventDefault()
        enabled[0]?.focus()
        break
      }

      case 'End': {
        event.preventDefault()
        enabled[enabled.length - 1]?.focus()
        break
      }

      default:
    }
  }

  element.addEventListener('keydown', onMenuKeydown)

  const open = (button: HTMLElement, columnKey: string) => {
    const actions = context.getActions(columnKey)
    if (actions.length === 0) {
      return
    }

    const nodes: Node[] = []
    for (const [index, action] of actions.entries()) {
      if (index > 0 && action.group !== actions[index - 1].group) {
        const divider = document.createElement('div')
        divider.classList.add(CLASS_NAME_MENU_DIVIDER)
        divider.setAttribute('role', 'separator')
        nodes.push(divider)
      }

      const item = document.createElement('button')
      item.type = 'button'
      item.classList.add(CLASS_NAME_MENU_ITEM)
      item.setAttribute('role', 'menuitem')
      if (action.icon) {
        const icon = document.createElement('span')
        icon.classList.add(CLASS_NAME_MENU_ITEM_ICON)
        icon.setAttribute('aria-hidden', 'true')
        icon.innerHTML = action.icon
        item.append(icon)
      }

      item.append(action.label)
      if (action.disabled) {
        item.setAttribute('aria-disabled', 'true')
      }

      item.addEventListener('click', () => {
        if (action.disabled) {
          return
        }

        const previousTrigger = trigger
        close()
        action.run()
        // The action may remove the trigger's header cell (hide column); the
        // check has to run after the scheduled re-render.
        requestAnimationFrame(() => {
          // An action may have moved focus deliberately (the filter dialog);
          // only restore it when it fell out of the grid.
          const active = document.activeElement
          if (active && active !== document.body && context.root.contains(active)) {
            return
          }

          if (previousTrigger?.isConnected) {
            previousTrigger.focus()
          } else {
            context.root.querySelector<HTMLElement>('[data-coreui-menu]')?.focus()
          }
        })
      })
      nodes.push(item)
    }

    element.replaceChildren(...nodes)

    trigger = button
    button.setAttribute('aria-expanded', 'true')
    button.setAttribute('aria-controls', element.id)
    context.root.append(element)

    const rootRect = context.root.getBoundingClientRect()
    const buttonRect = button.getBoundingClientRect()
    element.style.top = `${buttonRect.bottom - rootRect.top}px`
    // Right-align the menu to the trigger so it opens leftward — the menu button
    // sits at the cell's right edge, where a left-aligned menu would overflow.
    element.style.left = `${Math.max(buttonRect.right - rootRect.left - element.offsetWidth, 0)}px`

    document.addEventListener('pointerdown', onOutsidePointerDown, true)
    window.addEventListener('scroll', onWindowChange, true)
    window.addEventListener('resize', onWindowChange)
    items()[0]?.focus()
  }

  return {
    toggle(button, columnKey) {
      if (trigger === button) {
        close(true)
        return
      }

      close()
      open(button, columnKey)
    },
    close() {
      close()
    },
    dispose() {
      close()
      element.removeEventListener('keydown', onMenuKeydown)
    }
  }
}
